import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const MODEL_PRICES = {
  'gpt-5.6-sol': { input: 5.00, output: 30.00 },
  'gpt-5.6-terra': { input: 2.50, output: 15.00 },
  'gpt-5.6-luna': { input: 1.00, output: 6.00 },
  'gpt-6-astra': { input: 10.00, output: 50.00 },
  'claude-sonnet-4-20250514': { input: 3.00, output: 15.00 },
  'claude-haiku-4-5-20251001': { input: 0.80, output: 4.00 },
};

function calcCost(model, inp, out) {
  const p = MODEL_PRICES[model] || { input: 2.50, output: 10.00 };
  return (inp || 0) * p.input / 1000000 + (out || 0) * p.output / 1000000;
}

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ internal_name: '', display_login: '', password: '', role_description: '' });
  const [error, setError] = useState('');
  const [mainTab, setMainTab] = useState('users');
  const [listTab, setListTab] = useState('active');

  // Chat viewer state
  const [sessions, setSessions] = useState([]);
  const [filterUserId, setFilterUserId] = useState('');
  const [openSession, setOpenSession] = useState(null);
  const [sessionMsgs, setSessionMsgs] = useState([]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const u = await api.getUsers();
      setUsers(u);
    } catch {}
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  async function loadSessions(userId) {
    try {
      const s = await api.adminSessions(userId || '');
      setSessions(s);
    } catch {}
  }

  async function openSessionDetail(sessionId) {
    if (openSession === sessionId) { setOpenSession(null); return; }
    try {
      const msgs = await api.adminSession(sessionId);
      setSessionMsgs(msgs);
      setOpenSession(sessionId);
    } catch {}
  }

  async function doSearch() {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) return;
    setSearching(true);
    try {
      const r = await api.adminSearchChats(searchQuery.trim());
      setSearchResults(r);
    } catch {}
    setSearching(false);
  }

  function switchToChats() {
    setMainTab('chats');
    loadSessions();
  }

  function switchToSearch() {
    setMainTab('search');
    setSearchResults([]);
    setSearchQuery('');
  }

  async function openCreateModal() {
    try {
      const { name } = await api.getNextName();
      setForm({ internal_name: name, display_login: '', password: '', role_description: '' });
      setEditUser(null);
      setError('');
      setShowModal(true);
    } catch {}
  }

  function openEditModal(u) {
    setEditUser(u);
    setForm({ internal_name: u.internal_name, display_login: u.display_login, password: '', role_description: u.role_description || '' });
    setError('');
    setShowModal(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    try {
      if (editUser) {
        const updates = { internal_name: form.internal_name, display_login: form.display_login, role_description: form.role_description };
        if (form.password) updates.password = form.password;
        await api.updateUser(editUser.id, updates);
      } else {
        if (!form.display_login || !form.password) {
          setError('Логин и пароль обязательны');
          return;
        }
        await api.createUser(form);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  }

  async function toggleActive(user) {
    await api.updateUser(user.id, { active: user.active ? false : true });
    loadData();
  }

  const activeUsers = users.filter(u => u.active);
  const disabledUsers = users.filter(u => !u.active);
  const displayList = listTab === 'active' ? activeUsers : disabledUsers;

  // Group sessions by date
  const sessionsByDate = {};
  sessions.forEach(s => {
    const date = s.started_at ? s.started_at.slice(0, 10) : 'Неизвестно';
    if (!sessionsByDate[date]) sessionsByDate[date] = [];
    sessionsByDate[date].push(s);
  });

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`provider-btn ${mainTab === 'users' ? 'active' : ''}`} onClick={() => setMainTab('users')}>
            Сотрудники
          </button>
          <button className={`provider-btn ${mainTab === 'chats' ? 'active' : ''}`} onClick={switchToChats}>
            Чаты
          </button>
          <button className={`provider-btn ${mainTab === 'search' ? 'active' : ''}`} onClick={switchToSearch}>
            Проверить чаты
          </button>
        </div>
        {mainTab === 'users' && (
          <button className="btn-add" onClick={openCreateModal}>+ Добавить сотрудника</button>
        )}
      </div>

      {/* ============ USERS TAB ============ */}
      {mainTab === 'users' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button className={`provider-btn ${listTab === 'active' ? 'active' : ''}`} onClick={() => setListTab('active')}>
              Действующие ({activeUsers.length})
            </button>
            <button className={`provider-btn ${listTab === 'disabled' ? 'active' : ''}`} onClick={() => setListTab('disabled')}>
              Отключённые ({disabledUsers.length})
            </button>
          </div>
          <table className="users-table">
            <thead>
              <tr>
                <th>Системное имя</th>
                <th>Логин</th>
                <th>Роль</th>
                <th>Статус</th>
                <th>Создан</th>
                <th>{listTab === 'disabled' ? 'Отключён' : 'Последний вход'}</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {displayList.map(u => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 600 }}>{u.internal_name || u.name}</td>
                  <td>{u.display_login || u.email}</td>
                  <td>
                    <span className={`badge ${u.role}`}>{u.role}</span>
                    {u.role_description && <div style={{ fontSize: '0.75rem', color: 'var(--text2)', marginTop: 2 }}>{u.role_description.slice(0, 40)}{u.role_description.length > 40 ? '...' : ''}</div>}
                  </td>
                  <td><span className={`badge ${u.active ? 'active' : 'inactive'}`}>{u.active ? 'Активен' : 'Отключён'}</span></td>
                  <td style={{ color: 'var(--text2)', fontSize: '0.85rem' }}>{u.created_at ? u.created_at.slice(0, 10) : '—'}</td>
                  <td style={{ color: 'var(--text2)', fontSize: '0.85rem' }}>
                    {listTab === 'disabled' ? (u.disabled_at ? u.disabled_at.slice(0, 10) : '—') : (u.last_login || '—')}
                  </td>
                  <td>
                    <button className="btn-sm" onClick={() => openEditModal(u)}>Изменить</button>
                    <button className="btn-sm" onClick={() => toggleActive(u)}>
                      {u.active ? 'Отключить' : 'Включить'}
                    </button>
                  </td>
                </tr>
              ))}
              {displayList.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text2)', padding: 32 }}>Нет сотрудников</td></tr>
              )}
            </tbody>
          </table>
        </>
      )}

      {/* ============ CHATS TAB ============ */}
      {mainTab === 'chats' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
            <span style={{ color: 'var(--text2)', fontSize: '0.85rem' }}>Фильтр по сотруднику:</span>
            <select
              style={{ padding: '8px 12px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: '0.9rem' }}
              value={filterUserId}
              onChange={e => { setFilterUserId(e.target.value); loadSessions(e.target.value); }}
            >
              <option value="">Все сотрудники</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.internal_name} ({u.display_login})</option>
              ))}
            </select>
          </div>

          {sessions.length === 0 && (
            <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>Нет чатов</div>
          )}

          {Object.keys(sessionsByDate).map(date => (
            <div key={date} style={{ marginBottom: 20 }}>
              <h3 style={{ color: 'var(--text2)', fontSize: '0.85rem', marginBottom: 8, borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>{date}</h3>
              {sessionsByDate[date].map(s => (
                <div key={s.session_id}>
                  <div className="session-card" onClick={() => openSessionDetail(s.session_id)}>
                    <div className="session-meta">
                      <div>
                        <span style={{ fontWeight: 600 }}>{s.user_name}</span>
                        <span style={{ color: 'var(--text2)', fontSize: '0.85rem', marginLeft: 8 }}>{s.user_login}</span>
                        <span className={`badge ${s.provider === 'openai' ? 'manager' : 'admin'}`} style={{ marginLeft: 8 }}>
                          {s.provider === 'openai' ? 'ChatGPT' : 'Claude'}
                        </span>
                        <span style={{ color: 'var(--text2)', fontSize: '0.8rem', marginLeft: 8 }}>{s.model}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: '0.8rem', color: 'var(--text2)' }}>
                        <span>{s.message_count} сообщ.</span>
                        <span style={{ color: 'var(--accent2)' }}>${calcCost(s.model, s.total_input, s.total_output).toFixed(4)}</span>
                        <span>{s.started_at?.slice(11, 16)}</span>
                        <span>{openSession === s.session_id ? '▲' : '▼'}</span>
                      </div>
                    </div>
                    <div className="session-preview">{s.first_message || '—'}</div>
                  </div>
                  {openSession === s.session_id && (
                    <div className="session-messages">
                      {sessionMsgs.map((m, i) => (
                        <div key={i} className={`session-msg ${m.role}`}>
                          <div className="session-msg-label">
                            {m.role === 'user' ? 'Сотрудник' : 'AI'} — {m.created_at?.slice(11, 19)}
                            {m.role === 'assistant' && m.tokens_input > 0 && (
                              <span style={{ marginLeft: 8 }}>({m.tokens_input}+{m.tokens_output} tok, ${calcCost(m.model, m.tokens_input, m.tokens_output).toFixed(4)})</span>
                            )}
                          </div>
                          {m.content}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* ============ SEARCH TAB ============ */}
      {mainTab === 'search' && (
        <div>
          <h3 style={{ marginBottom: 12, color: 'var(--text2)' }}>Поиск по чатам сотрудников</h3>
          <p style={{ color: 'var(--text2)', fontSize: '0.85rem', marginBottom: 16 }}>
            Найдите личные переписки или задачи, не связанные с работой. Введите ключевые слова для поиска.
          </p>
          <div className="search-box">
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doSearch()}
              placeholder="Поиск: резюме, вакансия, подработка, личный, фриланс..."
            />
            <button onClick={doSearch} disabled={searching}>{searching ? 'Ищу...' : 'Найти'}</button>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {['резюме', 'вакансия', 'подработка', 'фриланс', 'личный', 'зарплата', 'собеседование', 'диплом', 'курсовая', 'реферат'].map(tag => (
              <button key={tag} className="btn-sm" onClick={() => { setSearchQuery(tag); }}
                style={{ fontSize: '0.75rem' }}>{tag}</button>
            ))}
          </div>

          {searchResults.length > 0 && (
            <div style={{ marginBottom: 12, color: 'var(--text2)', fontSize: '0.85rem' }}>
              Найдено: {searchResults.length} сообщений
              {(() => {
                const totalCost = searchResults.reduce((s, r) => s + calcCost(r.model, r.tokens_input, r.tokens_output), 0);
                return totalCost > 0 ? ` | Стоимость токенов: $${totalCost.toFixed(4)} (${(totalCost * 90).toFixed(2)} ₽)` : '';
              })()}
            </div>
          )}

          {searchResults.map((r, i) => (
            <div key={i} className="search-result">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.8rem' }}>
                <div>
                  <span style={{ fontWeight: 600 }}>{r.internal_name}</span>
                  <span style={{ color: 'var(--text2)', marginLeft: 8 }}>{r.display_login}</span>
                  <span className={`badge ${r.role === 'user' ? 'manager' : 'admin'}`} style={{ marginLeft: 8 }}>{r.role === 'user' ? 'Сотрудник' : 'AI'}</span>
                  <span className={`badge ${r.provider === 'openai' ? 'manager' : 'admin'}`} style={{ marginLeft: 4 }}>{r.provider === 'openai' ? 'GPT' : 'Claude'}</span>
                </div>
                <div style={{ color: 'var(--text2)' }}>
                  {r.created_at?.slice(0, 16).replace('T', ' ')}
                  {r.tokens_output > 0 && <span style={{ color: 'var(--accent2)', marginLeft: 8 }}>${calcCost(r.model, r.tokens_input, r.tokens_output).toFixed(4)}</span>}
                </div>
              </div>
              <div style={{ fontSize: '0.9rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'hidden' }}>
                {r.content}
              </div>
              {r.session_id && (
                <button className="btn-sm" style={{ marginTop: 8, fontSize: '0.75rem' }}
                  onClick={() => { setMainTab('chats'); setFilterUserId(r.user_id); loadSessions(r.user_id).then(() => openSessionDetail(r.session_id)); }}>
                  Открыть весь чат
                </button>
              )}
            </div>
          ))}

          {searchResults.length === 0 && searchQuery && !searching && (
            <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>Ничего не найдено</div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <form className="modal" onClick={e => e.stopPropagation()} onSubmit={handleSave}>
            <h3>{editUser ? 'Редактировать сотрудника' : 'Новый сотрудник'}</h3>
            {error && <div className="login-error">{error}</div>}
            <label>Системное имя</label>
            <input value={form.internal_name} onChange={e => setForm({ ...form, internal_name: e.target.value })} placeholder="Manager_1" />
            <label>Отображаемый логин (латиница)</label>
            <input value={form.display_login} onChange={e => setForm({ ...form, display_login: e.target.value })} placeholder="Ivanov" required={!editUser} />
            <label>{editUser ? 'Новый пароль (оставьте пустым если не меняете)' : 'Пароль'}</label>
            <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required={!editUser} />
            <label>Роль в организации</label>
            <textarea
              value={form.role_description}
              onChange={e => setForm({ ...form, role_description: e.target.value })}
              placeholder="Дизайнер выставочных стендов, 3D-визуализация..."
              rows={3}
            />
            <p style={{ color: 'var(--text2)', fontSize: '0.75rem', marginTop: 4 }}>
              Роль создаёт контекстную папку навыков в ИИ — нейросеть будет обучаться на основании общения с этим сотрудником в рамках его специализации.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Отмена</button>
              <button type="submit" className="btn-save">Сохранить</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
